import { computed, ref, watch } from '@vue/composition-api';
import usePersistedPerPage from '@/libs/utils/perPage';

// Notification
import { useToast } from 'vue-toastification/composition';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import router from '@/router';
import store from '@/store';
import { submittedToVaOptions, resolveSubmittedToVaLabel, resolveSubmittedToVaVariant } from './vaSubmission';
import {
  resolveProfileStatusVariant,
  resolveProfileYNVariant,
  statusOptions,
  uploadedCoeOptions,
  uploadedResumeOptions,
  uploadedTranscriptOptions,
  resolveProfileLocationStatus,
  resolveProfileSectionStatusVariant,
  resolveProfileProgramVariant,
  sectionStatusOptions,
  fundingSourceOptions,
  examVoucherOptions,
  yesNoOptions,
  yesNoActualOptions,
  locationOptions,
} from './profileOptions';

export default function useProfiles() {
  // Use toast
  const toast = useToast();

  const refProfileListTable = ref(null);

  // Table Handlers
  const tableColumnsList = [
    { key: 'id', sortable: true, stickyColumn: true, variant: 'primary', tdClass: 'sticky-primary', thClass: 'sticky-primary' },
    // { key: 'rep' },
    // { key: 'pathway', sortable: true, label: 'Preferred Program ' },
    { key: 'session', label: 'Cohort/Section', sortable: true },
    { key: 'status', sortable: true },
    { key: 'fundingSource', label: 'Funding', sortable: true },
    { key: 'uploadedResume', label: 'Resume', sortable: true },
    { key: 'uploadedCoe', label: 'COE', sortable: true },
    { key: 'uploadedTranscript', label: 'Transcript', sortable: true },
    { key: 'submittedToVa', label: 'VA Submission', sortable: true },
    { key: 'toc', label: 'TOC', sortable: true },
    { key: 'createdAt', label: 'Created', sortable: true },
    { key: 'actions' },
  ];

  // Table Handlers
  const tableColumnsListPending = [
    { key: 'id', sortable: true, stickyColumn: true, variant: 'primary', tdClass: 'sticky-primary', thClass: 'sticky-primary' },
    // { key: 'rep' },
    { key: 'preferred', sortable: true, label: 'Preferred Program ' },
    { key: 'session', label: 'Cohort/Section', sortable: true },
    { key: 'status', sortable: true },
    { key: 'fundingSource', label: 'Funding', sortable: true },
    { key: 'uploadedResume', label: 'Resume', sortable: true },
    { key: 'uploadedCoe', label: 'COE', sortable: true },
    { key: 'uploadedTranscript', label: 'Transcript', sortable: true },
    { key: 'submittedToVa', label: 'VA Submission', sortable: true },
    { key: 'toc', label: 'TOC', sortable: true },
    { key: 'createdAt', label: 'Created', sortable: true },
    { key: 'actions' },
  ];

  // Table Handlers
  const tableColumnsStudentView = [
    {
      key: 'session',
      label: 'Cohort/Section',
      sortable: true,
      // stickyColumn: true,
      // variant: 'primary',
      // tdClass: 'sticky-primary',
      // thClass: 'sticky-primary',
    },
    { key: 'status', sortable: true },
    { key: 'modality', sortable: true },
    { key: 'fundingSource', label: 'Funding', sortable: true },
    { key: 'compliance', label: 'Compliance', sortable: false },
    { key: 'retake', label: 'Retake', sortable: false },
    { key: 'uploadedResume', label: 'Resume', sortable: true },
    { key: 'uploadedCoe', label: 'COE', sortable: true },
    { key: 'toc', label: 'TOC', sortable: true },
    { key: 'uploadedTranscript', label: 'Transcript', sortable: true },
    { key: 'submittedToVa', label: 'VA Submission', sortable: true },
    { key: 'actions' },
  ];

  // if (router.currentRoute.query.subject) {
  //   tableColumnsListPending[2] = { key: 'subject', sortable: true };
  // }

  const {
    pathway: qPathway,
    subject: qSubject,
    search: qSearch,
    enrolled: qEnrolled,
    visibility: qVisibility,
    status: qStatus,
    funding: qFunding,
    resume: qResume,
    coe: qCoe,
    va: qVa,

    foresight: qForesight,

    perPage: qPerPage,
    page: qCurrentPage,
    sortBy: qSortBy,
    sortDesc: qSortDesc,
  } = router.currentRoute.query;

  const perPage = usePersistedPerPage('profiles', 10, qPerPage);
  const totalProfiles = ref(0);
  const totalPageCount = ref(0);
  const currentPage = ref(qCurrentPage || 1);
  const perPageOptions = [10, 25, 50, 100];
  const searchQuery = ref(qSearch);
  const sortBy = ref(qSortBy || 'createdAt');
  const isSortDirDesc = ref(qSortDesc || true);
  const hasFetched = ref(false);

  const visibilityFilter = ref(qVisibility);
  const statusFilter = ref(qStatus);
  const foresightFilter = ref(qForesight);
  const includeEnrolledFilter = ref(qEnrolled);
  const createAtStartFilter = ref(null);
  const createAtEndFilter = ref(null);
  const fundingFilter = ref(qFunding);
  const resumeFilter = ref(qResume);
  const coeFilter = ref(qCoe);
  const submissionFilter = ref(qVa);
  const studentFilter = ref(null);

  const dataMeta = computed(() => {
    const localItemsCount = refProfileListTable.value ? refProfileListTable.value.localItems.length : 0;
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalProfiles.value,
    };
  });

  const refetchData = () => {
    refProfileListTable.value.refresh();
  };

  watch(
    [
      // currentPage,
      perPage,
      searchQuery,
      includeEnrolledFilter,
      statusFilter,
      createAtStartFilter,
      createAtEndFilter,
      fundingFilter,
      resumeFilter,
      coeFilter,
      submissionFilter,
      studentFilter,
      foresightFilter,
      sortBy,
      isSortDirDesc,
    ],
    () => {
      const query = {
        search: searchQuery.value,

        enrolled: includeEnrolledFilter.value,
        status: statusFilter.value,
        funding: fundingFilter.value,
        resume: resumeFilter.value,
        coe: coeFilter.value,
        va: submissionFilter.value,

        student: studentFilter.value,

        foresight: foresightFilter.value,

        perPage: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
      };

      if (!query.search) delete query.search;
      if (!query.status) delete query.status;
      if (!query.enrolled) delete query.enrolled;
      if (!query.funding) delete query.funding;
      if (!query.resume) delete query.resume;
      if (!query.coe) delete query.coe;
      if (!query.va) delete query.va;

      if (query.page === '1' || query.page === 1) delete query.page;
      if (query.perPage === '10' || query.perPage === 10) delete query.perPage;
      if (query.sortBy === 'createdAt') delete query.sortBy;
      if (query.sortDesc === 'true' || query.sortDesc === true) delete query.sortDesc;

      if (hasFetched.value) {
        refetchData();
      }

      // router.replace({
      //   query,
      // });
    },
  );

  const fetchProfiles = (ctx, callback) => {
    hasFetched.value = false;
    const sbv = sortBy.value === 'id' ? '_id' : sortBy.value;
    const sb = `${sbv}:${isSortDirDesc.value ? 'desc' : 'asc'}`;

    const pl = {
      search: searchQuery.value,
      limit: perPage.value,
      page: currentPage.value,
      sortBy: sb,
      populate: 'reassignFrom,compliance',
      // populate: 'student,session,pathway,subject,representative',
    };

    if (foresightFilter.value) {
      console.log(`applying foresight ${foresightFilter.value}`);
      pl.foresight = Number(foresightFilter.value);
    }

    if (visibilityFilter.value) {
      pl.visibility = visibilityFilter.value;
    }
    if (statusFilter.value) {
      pl.status = statusFilter.value;
    }
    if (includeEnrolledFilter.value === 'no') {
      pl.session = 'null';
      pl.pending = true;
    }
    if (createAtStartFilter.value) {
      pl.createAtStart = createAtStartFilter.value;
    }
    if (createAtEndFilter.value) {
      pl.createAtEnd = createAtEndFilter.value;
    }

    if (fundingFilter.value) {
      pl.fundingSource = fundingFilter.value;
    }

    if (resumeFilter.value) {
      pl.uploadedResume = resumeFilter.value;
    }
    if (coeFilter.value) {
      pl.uploadedCoe = coeFilter.value;
    }
    if (submissionFilter.value) {
      pl.submittedToVa = submissionFilter.value;
    }
    if (studentFilter.value) {
      pl.student = studentFilter.value;
    }

    /*
    resumeFilter,
    coeFilter,
    submissionFilter, */

    store
      .dispatch('rosterStoreModule/fetchProfiles', pl)
      .then((response) => {
        const { results, totalResults, totalPages } = response.data;

        if (totalResults === 1) callback(results.map((result) => ({ ...result, _showDetails: result.session })));
        else callback(results);

        hasFetched.value = true;
        totalProfiles.value = totalResults;
        totalPageCount.value = totalPages;
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error fetching profiles list-pending',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      });
  };

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveUserRoleVariant = (role) => {
    if (role === 'subscriber') return 'info';
    if (role === 'author') return 'warning';
    if (role === 'maintainer') return 'success';
    if (role === 'editor') return 'info';
    if (role === 'admin') return 'danger';
    return 'info';
  };

  const resolveUserRoleIcon = (role) => {
    if (role === 'subscriber') return 'ProfileIcon';
    if (role === 'author') return 'SettingsIcon';
    if (role === 'maintainer') return 'DatabaseIcon';
    if (role === 'editor') return 'Edit2Icon';
    if (role === 'admin') return 'ServerIcon';
    return 'ProfileIcon';
  };

  const resolveProfileVisibilityVariant = (status) => {
    if (status === 'internal') return 'warning';
    if (status === 'public') return 'success';
    return 'info';
  };

  const resolveProfileOnboardingStatus = (status) => {
    if (status === 'invited') return 'info';
    if (status === 'onboarded') return 'success';
    return 'info';
  };

  const resolveProfilePaymentStatusVariant = (status) => {
    if (status === 'unpaid') return 'danger';
    if (status === 'partially_paid') return 'warning';
    if (status === 'payment_plan') return 'info';
    if (status === 'paid') return 'success';
    return 'info';
  };

  const resolveComplianceScoreVariant = ({ score, status } = { score: 0, status: 'unknown' }) => {
    if (status === 'credited') return 'success';
    if (status === 'withdrew' || status === 'terminated') return 'danger';
    if (score < 0.6) return 'light-danger';
    if (score < 0.7) return 'light-warning';
    if (score < 0.8) return 'light-info';
    if (score < 0.9) return 'light-success';
    return 'light-success';
  };

  const foresightOptions = [
    { label: 'Unlimited', value: null },
    { label: '+1 week', value: '7' },
    { label: '+2 weeks', value: '14' },
    { label: '+1 month', value: '30' },
    { label: '+3 months', value: '90' },
  ];

  const visibilityOptions = [
    { label: 'Internal', value: 'internal' },
    { label: 'Student', value: 'student' },
  ];

  const paymentStatusOptions = [
    { label: 'Unpaid', value: 'unpaid' },
    { label: 'Partially Paid', value: 'partially_paid' },
    { label: 'Payment Plan', value: 'payment_plan' },
    { label: 'Paid', value: 'paid' },
  ];

  return {
    fetchProfiles,
    tableColumnsList,
    tableColumnsListPending,
    tableColumnsStudentView,
    perPage,
    currentPage,
    totalProfiles,
    totalPageCount,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refProfileListTable,

    resolveUserRoleVariant,
    resolveUserRoleIcon,
    resolveProfileVisibilityVariant,
    resolveProfileStatusVariant,
    resolveProfileSectionStatusVariant,
    resolveProfileLocationStatus,

    resolveProfileProgramVariant,
    resolveProfileYNVariant,
    resolveProfilePaymentStatusVariant,
    resolveProfileOnboardingStatus,
    resolveComplianceScoreVariant,

    refetchData,

    // Extra Filters
    visibilityFilter,
    statusFilter,
    foresightFilter,
    includeEnrolledFilter,
    createAtStartFilter,
    createAtEndFilter,
    fundingFilter,

    resumeFilter,
    coeFilter,
    submissionFilter,

    studentFilter,

    //

    statusOptions,
    foresightOptions,
    visibilityOptions,
    fundingSourceOptions,
    paymentStatusOptions,
    submittedToVaOptions,
    resolveSubmittedToVaLabel,
    resolveSubmittedToVaVariant,
    uploadedCoeOptions,
    uploadedResumeOptions,
    uploadedTranscriptOptions,
    examVoucherOptions,
    yesNoOptions,
    yesNoActualOptions,
    sectionStatusOptions,
    locationOptions,
  };
}
